import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Users, Edit2, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import API_BASE_URL, { API_ENDPOINTS } from '../../config';
import { getSharedCollections } from '../../services/collectionSharingService';
import { useAppResume } from '../../hooks/useAppResume';
import { recordNetworkFailure, recordNetworkSuccess } from '../../hooks/useNetworkRecovery';
import ModalCloseButton from '../ModalCloseButton';
import { useMode } from '../../contexts/ModeContext';
import { useSpace } from '../../contexts/SpaceContext';
import '../../styles/components/collectionSelector.css';

// localStorage key for recent collection access times
const RECENT_COLLECTIONS_KEY = 'tidy_recent_collections';
// localStorage key for cached collections data (space-specific)
const cachedCollectionsKey = (spaceId?: number) =>
  spaceId ? `tidy_cached_collections_${spaceId}` : 'tidy_cached_collections';

// Get recent access times from localStorage
const getRecentAccessTimes = (): Record<string, number> => {
  try {
    const stored = localStorage.getItem(RECENT_COLLECTIONS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Record access time for a collection
const recordCollectionAccess = (collectionId: string) => {
  try {
    const times = getRecentAccessTimes();
    times[collectionId] = Date.now();
    // Keep only the last 50 entries to prevent unbounded growth
    const entries = Object.entries(times).sort(([, a], [, b]) => b - a).slice(0, 50);
    localStorage.setItem(RECENT_COLLECTIONS_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // Ignore localStorage errors
  }
};

export type CollectionType = 'all' | string;
export type DisplayMode = 'list' | 'checklist' | 'stock';

// Centralized pseudo-collection definitions
// Each pseudo-collection has: id (internal), name (display), description (explanatory text)
export const PSEUDO_COLLECTIONS = {
  all: {
    id: 'all',
    name: 'All Items',
    description: 'Complete Inventory',
  },
  wandering: {
    id: 'wandering',
    name: 'Nomads',
    description: 'Wandering Items',
  },
  shared_with_me: {
    id: 'shared_with_me',
    name: 'Shared Items',
    description: 'Shared With Me',
  },
} as const;

interface Collection {
  id: number;
  name: string;
  is_system: boolean;
  is_template?: boolean;
  is_checklist?: boolean;
  stock_keeping?: boolean;
  icon?: string;
  color?: string;
  item_count?: number;
  collection_type?: string;
}

interface SharedCollection {
  id: number;
  name: string;
  owner_name: string;
  group_name: string;
  permission: 'view' | 'edit';
  item_count: number;
  collection_type?: string;
}

interface CollectionSelectorProps {
  selected: CollectionType;
  onChange: (collection: CollectionType) => void;
  onManageCollections?: () => void;
  onCreateCollection?: () => void;
  onRenameCollection?: (collectionId: number, currentName: string) => void;
  onDeleteCollection?: (collectionId: number, name: string) => void;
  onShareCollection?: (collectionId: number) => void;
  onEmptyCollection?: (collectionId: number, name: string) => void;
  onShowCollectionDetail?: (collectionId: number | undefined, collectionName: string, itemCount: number) => void;
  refreshTrigger?: number; // Increment to trigger refresh
  allItemsCount?: number; // Total count of all items (from InventoryView)
  wanderingItemsCount?: number; // Count of items not in any collection
  renderAsDropdownOnly?: boolean; // When true, only render the dropdown menu (no trigger button)
  renderAsBottomSheet?: boolean; // When true, render as a bottom sheet modal
  isBottomSheetOpen?: boolean; // Control bottom sheet open state externally
  onBottomSheetClose?: () => void; // Callback when bottom sheet should close
  hideKitchenCollections?: boolean; // Hide grocery-type collections (non-Kitchen spaces)
  onOpenAppContext?: () => void; // Open the Data Space selector sheet
}

const CollectionSelector: React.FC<CollectionSelectorProps> = ({
  selected,
  onChange,
  onManageCollections,
  onCreateCollection,
  onRenameCollection,
  onDeleteCollection,
  onShareCollection,
  onEmptyCollection,
  onShowCollectionDetail,
  refreshTrigger,
  allItemsCount,
  wanderingItemsCount,
  renderAsDropdownOnly = false,
  renderAsBottomSheet = false,
  isBottomSheetOpen = false,
  onBottomSheetClose,
  hideKitchenCollections = false,
  onOpenAppContext
}) => {
  const { token } = useAuth();
  const { activeMode, setActiveMode } = useMode();
  const { activeSpace, spaces } = useSpace();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [sharedCollections, setSharedCollections] = useState<SharedCollection[]>([]);
  const [sharedWithMeCount, setSharedWithMeCount] = useState<number>(0);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const prevCountsRef = useRef<{ all?: number; wandering?: number }>({});

  useEffect(() => {
    fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchCollections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Detect when counts change and invalidate cache to force refresh
  useEffect(() => {
    const prevAll = prevCountsRef.current.all;
    const prevWandering = prevCountsRef.current.wandering;
    
    // If counts have changed, invalidate cache and refetch
    if ((prevAll !== undefined && prevAll !== allItemsCount) ||
        (prevWandering !== undefined && prevWandering !== wanderingItemsCount)) {
      console.log('[CollectionSelector] Counts changed, invalidating cache and refetching');
      console.log(`  All: ${prevAll} → ${allItemsCount}, Wandering: ${prevWandering} → ${wanderingItemsCount}`);
      
      // Clear cached collections to force fresh fetch
      localStorage.removeItem(cachedCollectionsKey(activeSpace?.id));
      
      // Fetch fresh collections with updated counts
      fetchCollections();
    }
    
    // Update previous counts
    prevCountsRef.current = {
      all: allItemsCount,
      wandering: wanderingItemsCount
    };
  }, [allItemsCount, wanderingItemsCount]);

  // Listen for collections-updated event (e.g., after copying from template)
  useEffect(() => {
    const handleCollectionsUpdated = () => {
      fetchCollections();
    };
    
    window.addEventListener('collections-updated', handleCollectionsUpdated);
    
    return () => {
      window.removeEventListener('collections-updated', handleCollectionsUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh collections when app resumes from background (iOS/Android)
  useAppResume(
    useCallback(() => {
      console.log('[CollectionSelector] App resumed, refreshing collections');
      fetchCollections();
    }, []),
    30000
  );

  // Fetch group name when selected is a "shared_with_group_" filter
  useEffect(() => {
    const fetchGroupName = async () => {
      if (selected.startsWith('shared_with_group_') && token) {
        const groupId = parseInt(selected.replace('shared_with_group_', ''));
        try {
          const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setGroupName(data.name);
          }
        } catch (error) {
          console.error('Failed to fetch group name:', error);
          setGroupName(null);
        }
      } else {
        setGroupName(null);
      }
    };
    
    fetchGroupName();
  }, [selected, token]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Refresh collections when bottom sheet opens to ensure fresh data
      console.log('[CollectionSelector] Bottom sheet opened, refreshing collections');
      fetchCollections();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Refresh collections when bottom sheet opens (for renderAsBottomSheet mode)
  useEffect(() => {
    if (renderAsBottomSheet && isBottomSheetOpen) {
      console.log('[CollectionSelector] Bottom sheet opened, refreshing collections');
      fetchCollections();
    }
  }, [isBottomSheetOpen, renderAsBottomSheet]);

  const fetchCollections = async () => {
    console.log(`[CollectionSelector] fetchCollections called, token exists: ${!!token}`);
    if (!token) {
      console.log('[CollectionSelector] No token, skipping fetch');
      return;
    }
    
    // Load cached data immediately for instant display
    let hasCachedData = false;
    try {
      const cached = localStorage.getItem(cachedCollectionsKey(activeSpace?.id));
      if (cached) {
        const { collections: cachedOwned, sharedCollections: cachedShared, sharedWithMeCount: cachedCount, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        // Use cache if less than 30 minutes old (increased from 5 minutes for better iOS resume experience)
        if (age < 30 * 60 * 1000) {
          console.log('[CollectionSelector] Using cached collections data (age: ' + Math.round(age / 1000) + 's)');
          setCollections(cachedOwned || []);
          setSharedCollections(cachedShared || []);
          setSharedWithMeCount(cachedCount || 0);
          hasCachedData = true;
        }
      }
    } catch (error) {
      console.error('[CollectionSelector] Failed to load cached collections:', error);
    }
    
    setLoading(true);
    try {
      // Add cache-busting timestamp to prevent stale data
      const cacheBuster = `_t=${Date.now()}`;
      
      // Helper to add timeout to fetch requests (10 seconds)
      const fetchWithTimeout = (url: string, options: RequestInit, timeoutMs = 10000) => {
        return Promise.race([
          fetch(url, options),
          new Promise<Response>((_, reject) => 
            setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
          )
        ]);
      };
      
      // Fetch owned, shared collections, and shared-with-me items count in parallel
      const spaceParam = activeSpace ? `&space_id=${activeSpace.id}` : '';
      const [ownedResponse, sharedCollectionsData, sharedWithMeResponse] = await Promise.all([
        fetchWithTimeout(`${API_ENDPOINTS.GET_COLLECTIONS(true)}${spaceParam}&${cacheBuster}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).catch((err) => {
          console.error('[CollectionSelector] Owned collections fetch failed:', err);
          return { ok: false, status: 0 } as Response;
        }),
        getSharedCollections(token).catch(() => []),
        fetchWithTimeout(`${API_BASE_URL}/api/sharing/items/my-items?access_type=shared_with_me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).catch((err) => {
          console.error('Failed to fetch shared-with-me items:', err);
          return null;
        })
      ]);

      console.log(`[CollectionSelector] Owned response status: ${ownedResponse.status}`);
      let ownedCollections = [];
      let ownedSuccess = false;
      
      if (ownedResponse.ok) {
        const data = await ownedResponse.json();
        console.log(`[CollectionSelector] Fetched ${data.collections?.length || 0} owned collections`);
        ownedCollections = data.collections || [];
        setCollections(ownedCollections);
        ownedSuccess = true;
      } else {
        console.error('[CollectionSelector] Failed to fetch owned collections:', ownedResponse.status);
        // Record network failure if status is 0 (network error)
        if (ownedResponse.status === 0) {
          recordNetworkFailure();
        }
        // If we have cached data, preserve it on error
        if (!hasCachedData) {
          setCollections([]);
        }
      }
      
      
      // getSharedCollections returns SharedCollection[] directly
      let sharedSuccess = false;
      if (sharedCollectionsData && Array.isArray(sharedCollectionsData)) {
        setSharedCollections(sharedCollectionsData as SharedCollection[]);
        sharedSuccess = true;
        console.log(`[CollectionSelector] Fetched ${sharedCollectionsData.length} shared collections`);
      } else if (!hasCachedData) {
        setSharedCollections([]);
      }
      
      // Get count of items shared with me
      let sharedCount = 0;
      let sharedCountSuccess = false;
      if (sharedWithMeResponse && sharedWithMeResponse.ok) {
        const sharedItems = await sharedWithMeResponse.json();
        const count = Array.isArray(sharedItems) ? sharedItems.length : 0;
        console.log('[CollectionSelector] Shared with me count:', count);
        sharedCount = count;
        setSharedWithMeCount(count);
        sharedCountSuccess = true;
      } else {
        console.error('[CollectionSelector] Shared with me response not ok:', sharedWithMeResponse?.status);
        // If we have cached data, preserve it on error
        if (!hasCachedData) {
          setSharedWithMeCount(0);
        }
      }
      
      // Check if ALL critical fetches succeeded
      const allCriticalSuccess = ownedSuccess;
      const hasAnySuccess = ownedSuccess || sharedSuccess || sharedCountSuccess;
      
      console.log(`[CollectionSelector] Fetch results - Owned: ${ownedSuccess}, Shared: ${sharedSuccess}, SharedCount: ${sharedCountSuccess}`);
      
      // Record network success if any fetch succeeded
      if (hasAnySuccess) {
        recordNetworkSuccess();
      }
      
      // CRITICAL FIX: Only cache if ALL critical fetches succeeded
      // This prevents partial data from being cached and causing incomplete collection lists
      if (allCriticalSuccess) {
        try {
          const cacheData = {
            collections: ownedCollections,
            sharedCollections: sharedCollectionsData || [],
            sharedWithMeCount: sharedCount,
            timestamp: Date.now()
          };
          localStorage.setItem(cachedCollectionsKey(activeSpace?.id), JSON.stringify(cacheData));
          console.log('[CollectionSelector] ✓ Cached complete collections data');
        } catch (error) {
          console.error('[CollectionSelector] Failed to cache collections:', error);
        }
      } else {
        console.warn('[CollectionSelector] ✗ NOT caching - incomplete data (owned:', ownedSuccess, ')');
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [token, activeSpace?.id, refreshTrigger]);

  const truncateGroupName = (name: string, maxLength: number = 10) => {
    if (name.length <= maxLength) return name;
    return name.substring(0, 8) + '...';
  };

  const truncateCollectionName = (name: string) => {
    if (name.length <= 10) return name;
    return name.substring(0, 8) + '...';
  };

  // In bottom sheet, hide grocery collections when NOT in kitchen mode (home/professional never show grocery)
  // When used as dropdown, respect the hideKitchenCollections prop
  const shouldHideGrocery = renderAsBottomSheet ? activeMode !== 'kitchen' : hideKitchenCollections;

  // Merge collections with the same name (memoized)
  const getMergedCollections = React.useMemo(() => {
    const merged: Array<{
      id: string; // Can be number (owned) or "shared-{id}" (shared) or "merged-{name}" (both)
      name: string;
      ownedId?: number;
      ownedCount?: number;
      sharedId?: number;
      sharedCount?: number;
      totalCount: number;
      isShared: boolean;
      owner_name?: string;
      group_name?: string;
    }> = [];

    // Add owned collections (filter out grocery-type collections when not in Kitchen mode)
    collections.filter(c => !c.is_system && (!shouldHideGrocery || c.collection_type !== 'grocery')).forEach(col => {
      merged.push({
        id: col.id.toString(),
        name: col.name,
        ownedId: col.id,
        ownedCount: col.item_count || 0,
        totalCount: col.item_count || 0,
        isShared: false
      });
    });

    // Merge or add shared collections
    sharedCollections.filter(c => !shouldHideGrocery || c.collection_type !== 'grocery').forEach(sharedCol => {
      const existing = merged.find(m => m.name.toLowerCase() === sharedCol.name.toLowerCase());
      if (existing) {
        // Check if this is the same collection (you own it and it's shared)
        if (existing.ownedId === sharedCol.id) {
          // Same collection - mark it as shared and store total count
          existing.isShared = true;
          existing.sharedCount = sharedCol.item_count; // Total count from backend
          existing.owner_name = sharedCol.owner_name;
          existing.group_name = sharedCol.group_name;
          existing.totalCount = sharedCol.item_count; // Show total count
          // Keep the original ID since it's the same collection
          // The breakdown will be calculated when items are fetched
        } else {
          // Different collections with same name - truly merged
          existing.sharedId = sharedCol.id;
          existing.sharedCount = sharedCol.item_count;
          existing.totalCount = 0; // Will be calculated from actual items
          existing.isShared = true;
          existing.owner_name = sharedCol.owner_name;
          existing.group_name = sharedCol.group_name;
          // Encode both IDs in the merged ID for easy retrieval
          existing.id = `merged-${existing.ownedId}-${sharedCol.id}`;
        }
      } else {
        // Add as new shared-only collection
        merged.push({
          id: `shared-${sharedCol.id}`,
          name: sharedCol.name,
          sharedId: sharedCol.id,
          sharedCount: sharedCol.item_count,
          totalCount: sharedCol.item_count,
          isShared: true,
          owner_name: sharedCol.owner_name,
          group_name: sharedCol.group_name
        });
      }
    });

    // Sort by recent access time (most recent first)
    const recentTimes = getRecentAccessTimes();
    merged.sort((a, b) => {
      const timeA = recentTimes[a.id] || 0;
      const timeB = recentTimes[b.id] || 0;
      // If both have access times, sort by most recent
      if (timeA && timeB) return timeB - timeA;
      // If only one has access time, it goes first
      if (timeA) return -1;
      if (timeB) return 1;
      // Otherwise, sort alphabetically
      return a.name.localeCompare(b.name);
    });

    return merged;
  }, [collections, sharedCollections, shouldHideGrocery]);

  const getSelectedLabel = () => {
    if (selected === 'all') return PSEUDO_COLLECTIONS.all.name;
    
    if (selected === 'wandering') return PSEUDO_COLLECTIONS.wandering.name;
    
    if (selected === 'shared_with_me') {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title={PSEUDO_COLLECTIONS.shared_with_me.name}>
          <Users size={16} />
          <span>{PSEUDO_COLLECTIONS.shared_with_me.name}</span>
        </span>
      );
    }
    
    // Check if this is a "shared with group" filter
    if (selected.startsWith('shared_with_group_')) {
      const displayName = groupName || 'Shared Items';
      const truncatedName = truncateGroupName(displayName);
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title={displayName}>
          <Users size={16} />
          <span>{truncatedName}</span>
        </span>
      );
    }
    
    // Check merged collections
    const mergedCollection = getMergedCollections.find(c => c.id === selected);
    if (mergedCollection) {
      const truncatedName = truncateCollectionName(mergedCollection.name);
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title={mergedCollection.name}>
          <span>{truncatedName}</span>
          {mergedCollection.isShared && <Users size={14} style={{ color: 'var(--color-accent)' }} />}
        </span>
      );
    }
    
    return 'All Items';
  };

  const handleSelect = (collectionId: CollectionType) => {
    // Record access time for recent sorting
    recordCollectionAccess(collectionId);
    onChange(collectionId);
    setIsOpen(false);
    if (onBottomSheetClose) {
      onBottomSheetClose();
    }
  };

  // Render the collection list content (shared between dropdown and bottom sheet)
  const renderCollectionList = () => (
    <>
      {/* All Items */}
      <button
        className={`collection-dropdown-item pseudo-collection ${selected === 'all' ? 'active' : ''}`}
        onClick={() => handleSelect('all')}
      >
        <div className="collection-item-info">
          <span className="collection-item-label">{PSEUDO_COLLECTIONS.all.name}</span>
        </div>
        {allItemsCount !== undefined && (
          <span className="collection-item-count">{allItemsCount}</span>
        )}
      </button>

      {/* Nomads - items not in any collection */}
      {wanderingItemsCount !== undefined && wanderingItemsCount > 0 && (
        <button
          className={`collection-dropdown-item pseudo-collection ${selected === 'wandering' ? 'active' : ''}`}
          onClick={() => handleSelect('wandering')}
        >
          <div className="collection-item-info">
            <span className="collection-item-label">{PSEUDO_COLLECTIONS.wandering.name}</span>
          </div>
          <span className="collection-item-count">{wanderingItemsCount}</span>
        </button>
      )}

      {/* Shared Items - only show if there are actual items shared with the user */}
      {sharedWithMeCount > 0 && (
        <button
          className={`collection-dropdown-item pseudo-collection ${selected === 'shared_with_me' ? 'active' : ''}`}
          onClick={() => handleSelect('shared_with_me')}
        >
          <div className="collection-item-info">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Users size={16} style={{ color: 'var(--color-accent)' }} />
              <span className="collection-item-label">Shared Items</span>
            </div>
          </div>
          <span className="collection-item-count">{sharedWithMeCount}</span>
        </button>
      )}

      {/* Merged Collections */}
      {getMergedCollections.length > 0 && (
        <>
          <div className="collection-dropdown-divider" />
          {getMergedCollections.map((collection: any) => (
            <div key={collection.id} className="collection-dropdown-item-wrapper">
              <button
                className={`collection-dropdown-item ${selected === collection.id ? 'active' : ''}`}
                onClick={() => handleSelect(collection.id)}
              >
                <div className="collection-item-info">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span className="collection-item-label">{collection.name}</span>
                    {collection.isShared && (
                      <Users size={14} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                    )}
                  </div>
                  {(() => {
                    if (collection.ownedCount !== undefined && collection.sharedCount !== undefined && collection.sharedId) {
                      return (
                        <span className="collection-item-meta">
                          {collection.ownedCount} yours • {collection.sharedCount} shared
                        </span>
                      );
                    }
                    if (collection.isShared && collection.ownedId && !collection.sharedId) {
                      const totalCount = collection.totalCount || collection.sharedCount || 0;
                      const yoursCount = collection.ownedCount || 0;
                      const othersCount = totalCount - yoursCount;
                      
                      if (othersCount > 0) {
                        return (
                          <span className="collection-item-meta">
                            {yoursCount} yours • {othersCount} from group
                          </span>
                        );
                      }
                      return (
                        <span className="collection-item-meta">
                          Shared via {collection.group_name}
                        </span>
                      );
                    }
                    if (collection.owner_name && collection.group_name && !collection.ownedId) {
                      return (
                        <span className="collection-item-meta">
                          {collection.owner_name} • {collection.group_name}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
                {collection.totalCount > 0 && <span className="collection-item-count">{collection.totalCount}</span>}
              </button>
              {collection.ownedId && onShowCollectionDetail && (
                <button
                  className="collection-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onBottomSheetClose) onBottomSheetClose();
                    onShowCollectionDetail(collection.ownedId!, collection.name, collection.totalCount || 0);
                  }}
                  aria-label="Edit collection"
                  title="Edit collection"
                >
                  ⚙️
                </button>
              )}
            </div>
          ))}
        </>
      )}


      {/* New Collection Button - only show in non-bottom-sheet modes */}
      {onShowCollectionDetail && !renderAsBottomSheet && (
        <>
          <div className="collection-dropdown-divider" />
          <button
            className="collection-dropdown-item collection-create-btn"
            onClick={() => {
              setIsOpen(false);
              if (onBottomSheetClose) onBottomSheetClose();
              onShowCollectionDetail(undefined, '', 0);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span className="collection-item-label">New Collection</span>
          </button>
        </>
      )}
    </>
  );

  // When renderAsBottomSheet is true, render as a bottom sheet modal using portal
  if (renderAsBottomSheet) {
    // Don't render anything if bottom sheet is closed - prevents flash during re-renders
    if (!isBottomSheetOpen) {
      return null;
    }

    const MODES: Array<{ id: 'home' | 'kitchen' | 'professional'; label: string }> = [
      { id: 'home', label: 'Home' },
      { id: 'kitchen', label: 'Kitchen' },
      { id: 'professional', label: 'Professional' },
    ];

    const bottomSheetContent = (
      <div 
        className="collection-bottom-sheet-overlay" 
        onClick={onBottomSheetClose}
      >
        <div 
          className="collection-bottom-sheet"
          onClick={(e) => e.stopPropagation()}
        >
          <ModalCloseButton onClick={() => onBottomSheetClose?.()} style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 1 }} />
          <div className="collection-bottom-sheet-handle" />
          <div className="collection-bottom-sheet-header">
            <button
              className="collection-sheet-header-space-btn"
              onClick={() => {
                if (onBottomSheetClose) onBottomSheetClose();
                if (onOpenAppContext) onOpenAppContext();
              }}
            >
              <div className="space-name-with-chevron">
                <span className="collection-sheet-space-name">{activeSpace?.name || 'Primary Space'}</span>
                <ChevronDown size={20} className="space-header-chevron" />
              </div>
            </button>
          </div>

          {activeMode !== 'kitchen' && (
            <div className="collection-bottom-sheet-content">
              {renderCollectionList()}
            </div>
          )}

          {/* Fixed footer: New Collection + Modes */}
          <div className="collection-bottom-sheet-footer">
            {activeMode !== 'kitchen' && onShowCollectionDetail && (
              <button
                className="collection-bottom-sheet-new-btn"
                onClick={() => {
                  if (onBottomSheetClose) onBottomSheetClose();
                  onShowCollectionDetail(undefined, '', 0);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span>New Collection</span>
              </button>
            )}

            <div className="collection-sheet-modes">
              <div className="collection-sheet-modes-chips full-width">
                {MODES.map(mode => (
                  <button
                    key={mode.id}
                    className={`collection-sheet-mode-chip ${activeMode === mode.id ? 'active' : ''}`}
                    onClick={() => setActiveMode(mode.id)}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
    
    // Use portal to render at document body level to avoid clipping issues
    return ReactDOM.createPortal(bottomSheetContent, document.body);
  }

  // When renderAsDropdownOnly is true, just render the dropdown menu directly
  if (renderAsDropdownOnly) {
    return (
        <div className="collection-dropdown-menu">
          {/* All Items */}
          <button
            className={`collection-dropdown-item pseudo-collection ${selected === 'all' ? 'active' : ''}`}
            onClick={() => handleSelect('all')}
          >
            <div className="collection-item-info">
              <span className="collection-item-label">{PSEUDO_COLLECTIONS.all.name}</span>
                </div>
            {(() => {
              const totalCount = allItemsCount ?? getMergedCollections.reduce((sum: number, col: any) => sum + col.totalCount, 0);
              return totalCount > 0 ? <span className="collection-item-count">{totalCount}</span> : null;
            })()}
          </button>

          {/* Nomads - items not in any collection */}
          {wanderingItemsCount !== undefined && wanderingItemsCount > 0 && (
            <button
              className={`collection-dropdown-item pseudo-collection ${selected === 'wandering' ? 'active' : ''}`}
              onClick={() => handleSelect('wandering')}
            >
              <div className="collection-item-info">
                <span className="collection-item-label">{PSEUDO_COLLECTIONS.wandering.name}</span>
                    </div>
              <span className="collection-item-count">{wanderingItemsCount}</span>
            </button>
          )}

          {/* Shared Items - only show if there are actual items shared with the user */}
          {sharedWithMeCount > 0 && (
            <button
              className={`collection-dropdown-item pseudo-collection ${selected === 'shared_with_me' ? 'active' : ''}`}
              onClick={() => handleSelect('shared_with_me')}
            >
              <div className="collection-item-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Users size={16} style={{ color: 'var(--color-accent)' }} />
                  <span className="collection-item-label">{PSEUDO_COLLECTIONS.shared_with_me.name}</span>
                </div>
                  </div>
            </button>
          )}

          {/* Merged Collections */}
          {getMergedCollections.length > 0 && (
            <>
              <div className="collection-dropdown-divider" />
              {getMergedCollections.map((collection: any) => (
                <div key={collection.id} className="collection-dropdown-item-wrapper">
                  <button
                    className={`collection-dropdown-item ${selected === collection.id ? 'active' : ''}`}
                    onClick={() => handleSelect(collection.id)}
                  >
                    <div className="collection-item-info">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="collection-item-label">{collection.name}</span>
                        {collection.isShared && (
                          <Users size={14} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                        )}
                      </div>
                      {(() => {
                        // Show breakdown for truly merged collections (different IDs)
                        if (collection.ownedCount !== undefined && collection.sharedCount !== undefined && collection.sharedId) {
                          return (
                            <span className="collection-item-meta">
                              {collection.ownedCount} yours • {collection.sharedCount} shared
                            </span>
                          );
                        }
                        // Show "Shared" for owned collections that are also shared (same ID)
                        if (collection.isShared && collection.ownedId && !collection.sharedId) {
                          // Calculate breakdown: total - yours = from others
                          const totalCount = collection.totalCount || collection.sharedCount || 0;
                          const yoursCount = collection.ownedCount || 0;
                          const othersCount = totalCount - yoursCount;
                          
                          if (othersCount > 0) {
                            return (
                              <span className="collection-item-meta">
                                {yoursCount} yours • {othersCount} from group
                              </span>
                            );
                          }
                          return (
                            <span className="collection-item-meta">
                              Shared via {collection.group_name}
                            </span>
                          );
                        }
                        // Show owner/group for shared-only collections
                        if (collection.owner_name && collection.group_name && !collection.ownedId) {
                          return (
                            <span className="collection-item-meta">
                              {collection.owner_name} • {collection.group_name}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    {/* Only show total count for non-merged collections */}
                    {collection.totalCount > 0 && <span className="collection-item-count">{collection.totalCount}</span>}
                  </button>
                  {collection.ownedId && onShowCollectionDetail && (
                    <button
                      className="collection-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Open detail modal and close selector immediately
                        onShowCollectionDetail(collection.ownedId!, collection.name, collection.totalCount || 0);
                        setIsOpen(false);
                      }}
                      aria-label="Edit collection"
                      title="Edit collection"
                    >
                      ⚙️
                    </button>
                  )}
                </div>
              ))}
            </>
          )}

          {/* New Collection Button */}
          {onShowCollectionDetail && (
            <>
              <div className="collection-dropdown-divider" />
              <button
                className="collection-dropdown-item collection-create-btn"
                onClick={() => {
                  // Open detail modal and close selector immediately
                  onShowCollectionDetail(undefined, '', 0);
                  setIsOpen(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span className="collection-item-label">New Collection</span>
              </button>
            </>
          )}
        </div>
    );
  }

  // Normal rendering with trigger button
  return (
    <div className="collection-selector-dropdown" ref={dropdownRef}>
      <button
        className="collection-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="collection-dropdown-label">{getSelectedLabel()}</span>
        <svg 
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
          className={`collection-dropdown-arrow ${isOpen ? 'open' : ''}`}
        >
          <path d="M3 4.5L6 7.5L9 4.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="collection-dropdown-menu">
          {/* All Items */}
          <button
            className={`collection-dropdown-item pseudo-collection ${selected === 'all' ? 'active' : ''}`}
            onClick={() => handleSelect('all')}
          >
            <div className="collection-item-info">
              <span className="collection-item-label">{PSEUDO_COLLECTIONS.all.name}</span>
                </div>
            {(() => {
              const totalCount = allItemsCount ?? getMergedCollections.reduce((sum: number, col: any) => sum + col.totalCount, 0);
              return totalCount > 0 ? <span className="collection-item-count">{totalCount}</span> : null;
            })()}
          </button>

          {/* Nomads - items not in any collection */}
          {wanderingItemsCount !== undefined && wanderingItemsCount > 0 && (
            <button
              className={`collection-dropdown-item pseudo-collection ${selected === 'wandering' ? 'active' : ''}`}
              onClick={() => handleSelect('wandering')}
            >
              <div className="collection-item-info">
                <span className="collection-item-label">{PSEUDO_COLLECTIONS.wandering.name}</span>
                    </div>
              <span className="collection-item-count">{wanderingItemsCount}</span>
            </button>
          )}

          {/* Shared Items - only show if there are actual items shared with the user */}
          {sharedWithMeCount > 0 && (
            <button
              className={`collection-dropdown-item pseudo-collection ${selected === 'shared_with_me' ? 'active' : ''}`}
              onClick={() => handleSelect('shared_with_me')}
            >
              <div className="collection-item-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Users size={16} style={{ color: 'var(--color-accent)' }} />
                  <span className="collection-item-label">{PSEUDO_COLLECTIONS.shared_with_me.name}</span>
                </div>
                  </div>
            </button>
          )}

          {/* Merged Collections */}
          {getMergedCollections.length > 0 && (
            <>
              <div className="collection-dropdown-divider" />
              {getMergedCollections.map((collection: any) => (
                <div key={collection.id} className="collection-dropdown-item-wrapper">
                  <button
                    className={`collection-dropdown-item ${selected === collection.id ? 'active' : ''}`}
                    onClick={() => handleSelect(collection.id)}
                  >
                    <div className="collection-item-info">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="collection-item-label">{collection.name}</span>
                        {collection.isShared && (
                          <Users size={14} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                        )}
                      </div>
                      {(() => {
                        // Show breakdown for truly merged collections (different IDs)
                        if (collection.ownedCount !== undefined && collection.sharedCount !== undefined && collection.sharedId) {
                          return (
                            <span className="collection-item-meta">
                              {collection.ownedCount} yours • {collection.sharedCount} shared
                            </span>
                          );
                        }
                        // Show "Shared" for owned collections that are also shared (same ID)
                        if (collection.isShared && collection.ownedId && !collection.sharedId) {
                          // Calculate breakdown: total - yours = from others
                          const totalCount = collection.totalCount || collection.sharedCount || 0;
                          const yoursCount = collection.ownedCount || 0;
                          const othersCount = totalCount - yoursCount;
                          
                          if (othersCount > 0) {
                            return (
                              <span className="collection-item-meta">
                                {yoursCount} yours • {othersCount} from group
                              </span>
                            );
                          }
                          return (
                            <span className="collection-item-meta">
                              Shared via {collection.group_name}
                            </span>
                          );
                        }
                        // Show owner/group for shared-only collections
                        if (collection.owner_name && collection.group_name && !collection.ownedId) {
                          return (
                            <span className="collection-item-meta">
                              {collection.owner_name} • {collection.group_name}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    {/* Only show total count for non-merged collections */}
                    {collection.totalCount > 0 && <span className="collection-item-count">{collection.totalCount}</span>}
                  </button>
                  {collection.ownedId && onShowCollectionDetail && (
                    <button
                      className="collection-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Open detail modal and close selector immediately
                        onShowCollectionDetail(collection.ownedId!, collection.name, collection.totalCount || 0);
                        setIsOpen(false);
                      }}
                      aria-label="Edit collection"
                      title="Edit collection"
                    >
                      ⚙️
                    </button>
                  )}
                </div>
              ))}
            </>
          )}

          {/* New Collection Button */}
          {onShowCollectionDetail && (
            <>
              <div className="collection-dropdown-divider" />
              <button
                className="collection-dropdown-item collection-create-btn"
                onClick={() => {
                  // Open detail modal and close selector immediately
                  onShowCollectionDetail(undefined, '', 0);
                  setIsOpen(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span className="collection-item-label">New Collection</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CollectionSelector;
